import React, { useEffect, useState } from "react";
import { Skeleton } from "@mui/material";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import { useAuth } from "../../../context/AuthProvider";
import ReportView from "./ReportView";
import handleError from "../../../constants/handleError";
import AxiosInstance from "../../../utils/AxiosInstance";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import { usePermission } from "../../../context/PermissionContext";
import InvoiceTempPreview from "../settings/Invoice/InvoiceTempPreview";
import { formatCurrency } from "@heartinz/finance";
import TemplateModalLayout from "../../../templates/modules/TemplateModalLayout";

const ViewSaleTransactions = ({ isData, isLoading, isActive }) => {
  // console.log("isData", isData);
  const { isUser, updateSettings } = useAuth();
  const [reportModelOpen, setReportModelOpen] = useState(false);
  const [viewData, setViewData] = useState([]);
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { storeSettings } = usePermission();
  const [isViewTempModalOpen, setViewTempModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [selectTermalOption, setSelectTermalOption] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [selectedReturnOption, setSelectedReturnOption] = useState("RETURN");
  const [isViewRetrunModalOpen, setViewRetrunModalOpen] = useState(false);
  const templateId = storeSettings?.invoiceSettings?.template?.templateId;
  const handleSaleItems = async (saleId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`module/sale/${saleId}`);
      const results = response.data.results;
      return results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const handleSaleReturnItems = async (saleId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`module/salereturn/${saleId}`);
      return response.data.results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const handleSetTemplate = async (
    templateId,
    selectTermalOption,
    selectHalfA4Option,
    templateTheme,
    show = true,
  ) => {
    if (!checkNetwork()) return;
    try {
      const response = await AxiosInstance.patch(
        `module/storesettings/template`,
        {
          templateIdtemplateId: `${templateId}AND${selectTermalOption}AND${selectHalfA4Option}`,
          templateTheme,
        },
      );
      const results = response.data?.results?.invoiceSettings?.template;
      const id = results.templateId;
      const words = id.split("AND");
      setSelectedOption(words[0]);
      setSelectTermalOption(words[1]);
      setViewTempModalOpen(false);
      updateSettings({
        storeSettings: {
          invoiceSettings: {
            template: {
              templateId: results?.templateId,
              templateTheme: results?.templateTheme,
            },
            signature: results?.signature,
            termsAndConditions: results?.termsAndConditions,
          },
        },
      });
      {
        show && showAlert("success", "Template Selected...");
      }
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const handleSaleClose = () => {
    setViewModalOpen(false);
    setViewData(null);
    // navigate(location.pathname, { replace: true, state: {} });
  };

  const handleSaleReturnClose = () => {
    setViewRetrunModalOpen(false);
    setViewData(null);
  };

  const handleSaleView = async (item) => {
    // if (item.trackmode === "SALE") {
    const data = await handleSaleItems(item.saleid);
    if (data) {
      setViewData(data);
      setViewModalOpen(true);
      setViewTempModalOpen(true);
    }
    // }
  };

  const handleSaleReturnView = async (item) => {
    const data = await handleSaleReturnItems(item.saleReturnId);
    if (data) {
      setViewData(data);
      setViewRetrunModalOpen(true);
      setViewTempModalOpen(true);
    }
  };

  // const openSaleReportView = (item) => {
  //   setReportModelOpen(true);
  //   setViewData(item);
  // };
  const closeSaleReportView = () => {
    setReportModelOpen(false);
  };

  const paymentTotals = (isData?.combinedReportData || []).reduce(
    (totals, item) => {
      totals.CASH += Number(item?.paymentMethod?.CASH) || 0;
      totals.CARD += Number(item?.paymentMethod?.CARD) || 0;
      totals.ONLINE += Number(item?.paymentMethod?.ONLINE) || 0;
      totals.UPI += Number(item?.paymentMethod?.UPI) || 0;
      totals.PAYLATER += Number(item?.paymentMethod?.PAYLATER) || 0;

      return totals;
    },
    {
      CASH: 0,
      CARD: 0,
      ONLINE: 0,
      UPI: 0,
      PAYLATER: 0,
    },
  );

  const getPaymentClass = (value, paymentType, documentType) => {
    const amount = Number(value);

    // Zero should always be normal
    if (amount === 0) {
      return "";
    }

    // SALE_RETURN PayLater should always be normal
    if (paymentType === "PAYLATER" && documentType === "SALE_RETURN") {
      return "";
    }

    // SALE PayLater should be red when it has a value
    if (paymentType === "PAYLATER" && documentType === "SALE") {
      return "text-danger";
    }

    // Other payment types: only negative values are red
    if (amount < 0) {
      return "text-danger";
    }

    return "";
  };

  useEffect(() => {
    const id = storeSettings?.invoiceSettings?.template?.templateId || "";
    const words = id ? id?.split("AND") : [];
    setSelectedOption(words[0]);
    setSelectTermalOption(words[1]);
  }, []);

  return (
    <>
      <div className="table-responsive rtable">
        <table className="table table-sm table-bordered text-center">
          <tbody>
            <tr>
              <td className="fw-600">SALE TRANSACTIONS</td>
            </tr>
          </tbody>
        </table>

        <div className="d-flex rtable">
          <div className="w-50">
            <table className="table table-sm table-bordered">
              <tbody>
                <tr>
                  <td className="w-25">Name</td>
                  <td>
                    {isLoading ? (
                      <Skeleton variant="text" />
                    ) : (
                      isData?.combinedReportInfo?.store?.title || ""
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="w-25">From Date</td>
                  <td>
                    {isLoading ? (
                      <Skeleton variant="text" />
                    ) : isData?.combinedReportInfo?.fromdate ? (
                      DateConverter.ISOToDisplayFormat(
                        DateConverter.convertToTimeZone(
                          isData?.combinedReportInfo?.fromdate,
                          isUser?.timezone,
                        ),
                      ).date
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="w-25">To Date</td>
                  <td>
                    {isLoading ? (
                      <Skeleton variant="text" />
                    ) : isData?.combinedReportInfo?.todate ? (
                      DateConverter.ISOToDisplayFormat(
                        DateConverter.convertToTimeZone(
                          isData?.combinedReportInfo?.todate,
                          isUser?.timezone,
                        ),
                      ).date
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="w-25">No of Sales</td>
                  <td>
                    {isLoading ? (
                      <Skeleton variant="text" />
                    ) : (
                      `${isData?.saleReportSummary?.totalNoOfSale || 0}`
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="w-25">No of Return Sales</td>
                  <td>
                    {isLoading ? (
                      <Skeleton variant="text" />
                    ) : (
                      `${isData?.returnSaleReportSummary?.totalNoOfReturnSale || 0}`
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="w-10"></div>

          <div className="w-50 rtable">
            <table className="table table-sm table-bordered mb-0 text-center">
              <tbody>
                <tr>
                  <td className="w-25 fw-500">Base Total</td>
                  <td className="w-25 fw-500">TAX Total</td>
                  <td className="w-25 fw-500">Grand Total</td>
                </tr>
                <tr>
                  <td className="fw-600">
                    {isLoading ? (
                      <Skeleton variant="text" />
                    ) : (
                      `₹${CurrencyConverter.toCurrency(
                        Number(isData?.combinedReportSummary?.totalBasePrice) ||
                          0,
                        "INR",
                      )}`
                    )}
                  </td>
                  <td className="fw-600">
                    {isLoading ? (
                      <Skeleton variant="text" />
                    ) : (
                      `₹${CurrencyConverter.toCurrency(
                        Number(isData?.combinedReportSummary?.totalTaxAmount) ||
                          0,
                        "INR",
                      )}`
                    )}
                  </td>
                  <td className="fw-600">
                    {isLoading ? (
                      <Skeleton variant="text" />
                    ) : (
                      `₹${CurrencyConverter.toCurrency(
                        Number(isData?.combinedReportSummary?.totalSoldPrice) ||
                          0,
                        "INR",
                      )}`
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <table className="table table-sm table-bordered mb-3 rtable">
          <tbody>
            <tr>
              <td className="text-center">S No</td>
              <td className="text-center">Date</td>
              <td className="text-center">Voucher No</td>
              <td className="text-center">Name</td>
              <td className="text-center">Phone</td>
              {/* <td className="text-center">Tax Amount</td> */}
              <td className="text-center"> Total Sale</td>
              <td className="text-center"> Total Return</td>
              <td className="text-center">Cash</td>
              <td className="text-center">Card</td>
              <td className="text-center">Online</td>
              <td className="text-center">UPI</td>
              <td className="text-center">Paylater</td>
              <td className="text-center">Remark</td>
              {/* {isUser.stype === "MAIN" && (
                <td className="text-center">Generated By</td>
              )} */}
            </tr>
          </tbody>
          <tbody>
            {isLoading ? (
              [...Array(11)].map((_, index) => (
                <tr key={index}>
                  {[...Array(7)].map((__, i) => (
                    <td key={i} className="text-center">
                      <Skeleton variant="text" width={i === 0 ? 300 : 200} />
                    </td>
                  ))}
                </tr>
              ))
            ) : Array.isArray(isData?.combinedReportData) &&
              isData?.combinedReportData?.length > 0 ? (
              isData?.combinedReportData?.map((item, index) => (
                <tr
                  key={item?._id || index}
                  className="position-relative  cursor-poInter hover-row"
                  onClick={
                    item?.documentType === "SALE"
                      ? () => handleSaleView(item)
                      : () => handleSaleReturnView(item)
                  }
                >
                  <td className="text-center">{index + 1}</td>

                  {/* <td className="text-center">
                    {
                      DateConverter.ISOToDisplayFormat(
                        DateConverter.convertToTimeZone(
                          item?.saledate,
                          isUser?.timezone
                        )
                      ).date
                    } 
                  </td> */}
                  <td className="text-center">
                    <div className="d-flex flex-column">
                      <span className="text-nowrap text-heading fw-medium">
                        {
                          DateConverter.ISOToDisplayFormat(
                            DateConverter.convertToTimeZone(
                              item.documentDate,
                              isUser.timezone,
                            ),
                          ).date
                        }
                      </span>
                    </div>
                  </td>
                  <td className="text-center">
                    {item?.documentType === "SALE"
                      ? item?.saleno || "-"
                      : item?.saleReturnNo || "-"}
                  </td>
                  <td className="text-center">
                    {item?.documentType === "SALE"
                      ? item?.saleto?.customername || "-"
                      : item?.CustomerInfo?.name || "-"}
                  </td>

                  <td className="text-center">
                    {item?.documentType === "SALE"
                      ? item?.saleto?.customerphone
                        ? `+91 ${item.saleto.customerphone}`
                        : "-"
                      : item?.CustomerInfo?.phone
                        ? `+91 ${item.CustomerInfo.phone}`
                        : "-"}
                  </td>

                  {/* <td className="text-end">
                    {formatCurrency(
                      CurrencyConverter.toCurrency(
                        item?.documentType === "SALE"
                          ? item?.taxamount || 0
                          : item?.totalTaxAmount || 0,
                        "INR",
                      ),
                      "INR",
                      false,
                      true,
                    )}
                  </td> */}

                  <td className="text-end text-success fw-600">
                    {item?.documentType === "SALE" ? (
                      <>
                        {formatCurrency(
                          CurrencyConverter.toCurrency(
                            Number(item?.totalamount) || 0,
                            "INR",
                          ),
                          "INR",
                          false,
                          true,
                        )}
                      </>
                    ) : (
                      ""
                    )}
                  </td>

                  <td className="text-end text-danger fw-600">
                    {item?.documentType === "SALE_RETURN" ? (
                      <>
                        {formatCurrency(
                          CurrencyConverter.toCurrency(
                            Number(item?.totalReturnAmount) || 0,
                            "INR",
                          ),
                          "INR",
                          false,
                          true,
                        )}
                      </>
                    ) : (
                      ""
                    )}
                  </td>

                  <td
                    className={`text-end fw-600 ${getPaymentClass(
                      item?.paymentMethod?.CASH,
                      "CASH",
                    )}`}
                  >
                    {formatCurrency(
                      CurrencyConverter.toCurrency(
                        Number(item?.paymentMethod?.CASH) || 0,
                        "INR",
                      ),
                      "INR",
                      false,
                      true,
                    )}
                  </td>
                  <td
                    className={`text-end fw-600 ${getPaymentClass(
                      item?.paymentMethod?.CARD,
                      "CARD",
                    )}`}
                  >
                    {formatCurrency(
                      CurrencyConverter.toCurrency(
                        Number(item?.paymentMethod?.CARD) || 0,
                        "INR",
                      ),
                      "INR",
                      false,
                      true,
                    )}
                  </td>

                  <td
                    className={`text-end fw-600 ${getPaymentClass(
                      item?.paymentMethod?.ONLINE,
                      "ONLINE",
                    )}`}
                  >
                    {formatCurrency(
                      CurrencyConverter.toCurrency(
                        Number(item?.paymentMethod?.ONLINE) || 0,
                        "INR",
                      ),
                      "INR",
                      false,
                      true,
                    )}
                  </td>

                  <td
                    className={`text-end fw-600 ${getPaymentClass(
                      item?.paymentMethod?.UPI,
                      "UPI",
                    )}`}
                  >
                    {formatCurrency(
                      CurrencyConverter.toCurrency(
                        Number(item?.paymentMethod?.UPI) || 0,
                        "INR",
                      ),
                      "INR",
                      false,
                      true,
                    )}
                  </td>

                  <td
                    className={`text-end fw-600 ${getPaymentClass(
                      item?.paymentMethod?.PAYLATER,
                      "PAYLATER",
                      item?.documentType,
                    )}`}
                  >
                    {formatCurrency(
                      CurrencyConverter.toCurrency(
                        Number(item?.paymentMethod?.PAYLATER) || 0,
                        "INR",
                      ),
                      "INR",
                      false,
                      true,
                    )}
                  </td>
                  <td className="text-center">{item?.remarks || "-"}</td>
                  {/* {isUser.stype === "MAIN" && (
                    <td className="text-center">
                      {item.saleby?.storename || ""}
                    </td>
                  )} */}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center p-2">
                  <small className="text-muted">
                    No sale found in the store. add some customers to get
                    started.
                  </small>
                </td>
              </tr>
            )}
          </tbody>
          <tbody>
            <tr>
              <td colSpan="5" className="text-center"></td>

              {/* Total Sale */}
              <td className="text-end fw-600">
                {isLoading ? (
                  <Skeleton variant="text" />
                ) : (
                  formatCurrency(
                    CurrencyConverter.toCurrency(
                      isData?.saleReportSummary?.grandTotal || 0,
                      "INR",
                    ),
                    "INR",
                    false,
                    true,
                  )
                )}
              </td>

              {/* Total Return */}
              <td className="text-end fw-600">
                {isLoading ? (
                  <Skeleton variant="text" />
                ) : (
                  formatCurrency(
                    CurrencyConverter.toCurrency(
                      isData?.returnSaleReportSummary?.totalReturnAmount || 0,
                      "INR",
                    ),
                    "INR",
                    false,
                    true,
                  )
                )}
              </td>

              {/* Cash Total */}
              <td className="text-end fw-600">
                {isLoading ? (
                  <Skeleton variant="text" />
                ) : (
                  formatCurrency(
                    CurrencyConverter.toCurrency(paymentTotals.CASH, "INR"),
                    "INR",
                    false,
                    true,
                  )
                )}
              </td>

              {/* Card Total */}
              <td className="text-end fw-600">
                {isLoading ? (
                  <Skeleton variant="text" />
                ) : (
                  formatCurrency(
                    CurrencyConverter.toCurrency(paymentTotals.CARD, "INR"),
                    "INR",
                    false,
                    true,
                  )
                )}
              </td>

              {/* Online Total */}
              <td className="text-end fw-600">
                {isLoading ? (
                  <Skeleton variant="text" />
                ) : (
                  formatCurrency(
                    CurrencyConverter.toCurrency(paymentTotals.ONLINE, "INR"),
                    "INR",
                    false,
                    true,
                  )
                )}
              </td>

              {/* UPI Total */}
              <td className="text-end fw-600">
                {isLoading ? (
                  <Skeleton variant="text" />
                ) : (
                  formatCurrency(
                    CurrencyConverter.toCurrency(paymentTotals.UPI, "INR"),
                    "INR",
                    false,
                    true,
                  )
                )}
              </td>

              {/* Paylater Total */}
              <td className="text-end fw-600">
                {isLoading ? (
                  <Skeleton variant="text" />
                ) : (
                  formatCurrency(
                    CurrencyConverter.toCurrency(paymentTotals.PAYLATER, "INR"),
                    "INR",
                    false,
                    true,
                  )
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <table className="table table-sm table-bordered mb-0 rtable w-30 text-center">
          <thead>
            <tr>
              <td colSpan="3">Total Summary</td>
            </tr>
            <tr>
              {/* <td className="w-25">Tax %</td> */}
              {/* <td>Tax Amount</td> */}
              <td>Sale</td>
              <td>Return Sale</td>
              <td>Adjustment</td>
            </tr>
          </thead>
          <tbody>
            {!isLoading && (
              <tr className="font-bold border-t bg-gray-100">
                {/* <td>Total</td> */}
                {/* <td>
                          ₹
                          {CurrencyConverter.toCurrency(
                            isData.reportSummary.totalTaxAmount || 0,
                            "INR",
                          )}
                        </td> */}
                <td className="text-end">
                  {formatCurrency(
                    CurrencyConverter.toCurrency(
                      isData.saleReportSummary?.totalSoldPrice || 0,
                      "INR",
                    ),
                    "INR",
                    false,
                    true,
                  )}
                </td>
                <td className="text-end">
                  {formatCurrency(
                    CurrencyConverter.toCurrency(
                      isData.returnSaleReportSummary?.totalReturnAmount || 0,
                      "INR",
                    ),
                    "INR",
                    false,
                    true,
                  )}
                </td>
                <td className="text-end">
                  {formatCurrency(
                    CurrencyConverter.toCurrency(
                      isData.combinedReportSummary?.totalSoldPrice || 0,
                      "INR",
                    ),
                    "INR",
                    false,
                    true,
                  )}
                </td>
              </tr>
            )}
          </tbody>
          <tr>
            {/* <td className="w-25">Tax %</td> */}
            {/* <td>Tax Amount</td> */}
            <td>Sale Tax</td>
            <td>Return Sale Tax</td>
            <td>Tax Adjustment</td>
          </tr>
          <tbody>
            {!isLoading && (
              <tr className="font-bold border-t bg-gray-100">
                {/* <td>Total</td> */}
                {/* <td>
                          ₹
                          {CurrencyConverter.toCurrency(
                            isData.reportSummary.totalTaxAmount || 0,
                            "INR",
                          )}
                        </td> */}
                <td className="text-end">
                  {formatCurrency(
                    CurrencyConverter.toCurrency(
                      isData.saleReportSummary?.totalTaxAmount || 0,
                      "INR",
                    ),
                    "INR",
                    false,
                    true,
                  )}
                </td>
                <td className="text-end">
                  ₹
                  {formatCurrency(
                    CurrencyConverter.toCurrency(
                      isData.returnSaleReportSummary?.totalTaxAmount || 0,
                      "INR",
                    ),
                    "INR",
                    false,
                    true,
                  )}
                </td>
                <td className="text-end">
                  ₹
                  {formatCurrency(
                    CurrencyConverter.toCurrency(
                      isData.combinedReportSummary?.totalTaxAmount || 0,
                      "INR",
                    ),
                    "INR",
                    false,
                    true,
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!templateId && isViewTempModalOpen && (
        <InvoiceTempPreview
          isData={viewData}
          onClose={() => setViewTempModalOpen(false)}
          onAdd={handleSetTemplate}
        />
      )}

      {templateId && (isViewModalOpen || isViewRetrunModalOpen) && (
        <TemplateModalLayout
          isOpen={isViewModalOpen || isViewRetrunModalOpen}
          isData={viewData}
          selectedOption={
            isViewRetrunModalOpen ? selectedReturnOption : selectedOption
          }
          setSelectedOption={
            isViewRetrunModalOpen ? setSelectedReturnOption : setSelectedOption
          }
          onClose={
            isViewRetrunModalOpen ? handleSaleReturnClose : handleSaleClose
          }
          setSaleData={setViewData}
          isActive={isActive}
        />
      )}
      {reportModelOpen && (
        <ReportView onClose={closeSaleReportView} viewData={viewData} />
      )}
    </>
  );
};

export default ViewSaleTransactions;
